import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { FileText, Save, Globe, Info, Loader2, AlertTriangle } from 'lucide-react';

const LegalPagesManager = () => {
  const queryClient = useQueryClient();
  const [selectedSlug, setSelectedSlug] = useState('terms');
  const [formData, setFormData] = useState({ title: '', content: '', is_published: true });
  const [cookieText, setCookieText] = useState('');

  // 1. Fetch Pages (With Loading/Error states)
  const { data: pages = [], isLoading, isError, error } = useQuery({
    queryKey: ['cmsPages'],
    queryFn: async () => {
        try {
            const res = await axiosClient.get('/admin/cms/pages');
            return res || [];
        } catch (err) {
            console.error("API Error:", err);
            throw err;
        }
    }
  });

  // 2. Fetch Cookie Setting
  useQuery({
    queryKey: ['cookieSetting'],
    queryFn: async () => {
       const res = await axiosClient.get('/admin/cms/settings');
       if(res && res.cookie_consent_text) setCookieText(res.cookie_consent_text);
       return res;
    }
  });

  // Sync Form
  useEffect(() => {
    if (pages.length > 0) {
        const page = pages.find(p => p.slug === selectedSlug);
        if (page) {
            setFormData({ title: page.title, content: page.content, is_published: page.is_published });
        }
    }
  }, [selectedSlug, pages]);

  const savePageMutation = useMutation({
    mutationFn: (data) => axiosClient.post('/admin/cms/pages', { ...data, slug: selectedSlug }),
    onSuccess: () => { queryClient.invalidateQueries(['cmsPages']); alert("Page Updated Successfully!"); }
  });

  const saveCookieMutation = useMutation({
    mutationFn: (text) => axiosClient.post('/admin/cms/settings', { cookie_consent_text: text }),
    onSuccess: () => alert("Cookie Policy Updated!")
  });

  // --- LOADING STATE ---
  if (isLoading) return (
      <div className="flex h-64 items-center justify-center text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading Legal Pages...
      </div>
  );

  // --- ERROR STATE ---
  if (isError) return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center">
          <AlertTriangle className="w-6 h-6 mr-3" />
          <div>
              <h3 className="font-bold">Failed to load content</h3>
              <p className="text-sm">Check if Backend is running and Migration 078 was applied.</p>
              <p className="text-xs font-mono mt-1">{error?.message}</p>
          </div>
      </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
       {/* Sidebar List */}
       <div className="bg-white p-4 rounded-xl border border-slate-200 h-fit shadow-sm">
          <h3 className="font-bold text-slate-700 mb-4 flex items-center"><FileText className="w-5 h-5 mr-2 text-teal-600"/> Site Pages</h3>
          <div className="space-y-1">
             {pages.length === 0 ? (
                 <p className="text-sm text-slate-400 italic p-2">No pages found in DB.</p>
             ) : (
                 pages.map(p => (
                    <button 
                      key={p.slug}
                      onClick={() => setSelectedSlug(p.slug)}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex justify-between items-center ${selectedSlug === p.slug ? 'bg-blue-50 text-teal-600 border border-blue-100' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                       {p.title}
                       {p.is_published && <span className="w-2 h-2 rounded-full bg-green-500"></span>}
                    </button>
                 ))
             )}
          </div>
          
          <div className="mt-8 pt-6 border-t border-slate-100">
             <h4 className="font-bold text-slate-700 text-sm mb-3 flex items-center"><Globe className="w-4 h-4 mr-2 text-slate-500"/> Cookie Popup Text</h4>
             <textarea 
               className="w-full p-3 border border-slate-200 rounded-lg text-xs text-slate-600 focus:ring-2 focus:ring-blue-500 outline-none"
               value={cookieText}
               onChange={(e) => setCookieText(e.target.value)}
               rows={3}
               placeholder="We use cookies..."
             />
             <button 
                onClick={() => saveCookieMutation.mutate(cookieText)} 
                disabled={saveCookieMutation.isLoading}
                className="mt-3 w-full bg-slate-800 text-white text-xs font-bold py-2.5 rounded-lg hover:bg-slate-700 transition-colors"
             >
                {saveCookieMutation.isLoading ? 'Saving...' : 'Update Cookie Text'}
             </button>
          </div>
       </div>

       {/* Editor */}
       <div className="lg:col-span-2 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex justify-between items-center mb-6">
             <input 
               className="text-2xl font-bold text-slate-800 border-none outline-none w-full placeholder-slate-300" 
               value={formData.title}
               onChange={(e) => setFormData({...formData, title: e.target.value})}
               placeholder="Page Title"
             />
             <div className="flex items-center gap-4">
                <label className="flex items-center cursor-pointer select-none">
                   <input 
                        type="checkbox" 
                        className="w-4 h-4 text-teal-600 rounded focus:ring-blue-500"
                        checked={formData.is_published} 
                        onChange={(e) => setFormData({...formData, is_published: e.target.checked})} 
                    />
                   <span className="ml-2 text-sm font-medium text-slate-600">Published</span>
                </label>
                <button 
                    onClick={() => savePageMutation.mutate(formData)} 
                    disabled={savePageMutation.isLoading}
                    className="bg-blue-600 text-white px-5 py-2 rounded-lg flex items-center text-sm font-bold hover:bg-blue-700 shadow-lg shadow-blue-500/20 transition-all active:scale-95"
                >
                   {savePageMutation.isLoading ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Save className="w-4 h-4 mr-2"/> Save Page</>}
                </button>
             </div>
          </div>

          <div className="relative">
              <div className="absolute top-3 right-3 text-xs text-slate-300 pointer-events-none flex items-center bg-white px-2 rounded">
                 <Info className="w-3 h-3 mr-1"/> HTML Supported
              </div>
              <textarea 
                className="w-full h-[500px] p-5 border border-slate-200 rounded-xl font-mono text-sm leading-relaxed focus:ring-2 focus:ring-blue-500 outline-none text-slate-700 resize-none"
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                placeholder="<h1>Page Content...</h1>"
              />
          </div>
       </div>
    </div>
  );
};

export default LegalPagesManager;